/**
 * Client for /api/excerpts (#277). Types mirror server/src/lib/excerpts/service.ts.
 */
import type { JSONContent } from '@tiptap/core';
import { apiDelete, apiDeleteJson, apiGet, apiPost } from './api';

export type ExcerptKind = 'excerpt' | 'task' | 'note';
export type SourceType = 'meeting_instance' | 'task' | 'note' | 'scratchpad';
export type DestType = SourceType | 'scope' | 'meeting_series' | 'tag' | 'reference_doc' | 'person';

export interface ItemRef { type: DestType; id: string }
export interface Titled { type: string; id: string; title: string }

export interface ExcerptView {
  id: string;
  kind: ExcerptKind;
  status: string;
  live: boolean;
  deleted: boolean;
  fragment: JSONContent | null;
  plain_text: string | null;
  source: Titled;
  linked_entity_id: string | null;
  destinations: Titled[];
  created_at: string;
  updated_at: string;
  via?: Titled;
  /** Series lists only (#290): made in one of the series' meetings. */
  made_here?: boolean;
}

export const SOURCE_TYPES: readonly SourceType[] = ['meeting_instance', 'task', 'note', 'scratchpad'];
export const isSourceType = (t: string): t is SourceType => (SOURCE_TYPES as readonly string[]).includes(t);

/** The body field each source type autosaves, and its detail route. */
export const SOURCE_ROUTES: Record<SourceType, { path: string; field: string }> = {
  meeting_instance: { path: '/meeting-instances', field: 'note_content' },
  task: { path: '/tasks', field: 'description' },
  note: { path: '/notes', field: 'content' },
  scratchpad: { path: '/scratchpads', field: 'content' },
};

/**
 * #360: a series' "next meeting" as a destination. The server resolves it to
 * the series' earliest meeting from today on, creating the meeting when there
 * is none - on the series' next date, or on `date` for a series with no
 * schedule.
 */
export const NEXT_MEETING = 'next_meeting';
export interface NextMeetingDest { type: typeof NEXT_MEETING; id: string; date?: string }
export type SendDest = ItemRef | NextMeetingDest;

/** The meeting a next-meeting destination reached. */
export interface NextMeeting {
  series_id: string;
  series_name: string;
  id: string;
  title: string | null;
  meeting_date: string;
  meeting_time: string | null;
  /** True when sending created it. */
  created: boolean;
}

/** GET /meeting-series/:id/next-meeting: what "next meeting" would reach now. */
export interface NextMeetingPreview {
  series_id: string;
  series_name: string;
  archived: boolean;
  meeting: { id: string; title: string | null; meeting_date: string; meeting_time: string | null } | null;
  /** The date a new meeting would get; null when the series has no schedule and a date must be chosen. */
  next_date: string | null;
  next_time: string | null;
}

export const getNextMeeting = (seriesId: string) =>
  apiGet<NextMeetingPreview>(`/meeting-series/${encodeURIComponent(seriesId)}/next-meeting`);

/** "Wed Nov 11" for a YYYY-MM-DD date, read as a calendar day (no time zone shift). */
export function shortDate(date: string): string {
  const [y, m, d] = date.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return date;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).replace(',', '');
}

/** "1:1 Bob - Wed Nov 11" */
export const nextMeetingName = (m: Pick<NextMeeting, 'series_name' | 'meeting_date'>) => `${m.series_name} - ${shortDate(m.meeting_date)}`;

/** The meeting's page, under its series. */
export const nextMeetingUrl = (m: Pick<NextMeeting, 'series_id' | 'id'>) =>
  `/meetings/${encodeURIComponent(m.series_id)}/${encodeURIComponent(m.id)}`;

export const addNextMeeting = (id: string, seriesId: string, opts: { date?: string; placedMeetingId?: string } = {}) =>
  apiPost<ExcerptView & { next_meeting: NextMeeting }>(`/excerpts/${encodeURIComponent(id)}/destinations`, {
    type: NEXT_MEETING, id: seriesId,
    ...(opts.date ? { date: opts.date } : {}),
    ...(opts.placedMeetingId ? { placed_meeting_id: opts.placedMeetingId } : {}),
  });

export interface CreateExcerptInput {
  source_type: SourceType;
  source_id: string;
  kind: ExcerptKind;
  destinations?: SendDest[];
  /** The selected text: the history label, and the title for a task/note. */
  text?: string;
  title?: string;
  /** Body destinations whose open editor inserts the embed itself. */
  placed?: ItemRef[];
}

export const createExcerpt = (input: CreateExcerptInput) =>
  apiPost<{ id: string; linked_entity_id: string | null; next_meetings?: NextMeeting[] }>('/excerpts', input);

export const getExcerpts = (ids: string[]) =>
  apiGet<ExcerptView[]>(`/excerpts?ids=${ids.map(encodeURIComponent).join(',')}`);

export const listBySource = (type: SourceType, id: string) =>
  apiGet<ExcerptView[]>(`/excerpts/by-source/${type}/${encodeURIComponent(id)}`);

export const listByDestination = (type: DestType, id: string, rollup = false) =>
  apiGet<ExcerptView[]>(`/excerpts/by-destination/${type}/${encodeURIComponent(id)}${rollup ? '?rollup=true' : ''}`);

export const addDestination = (id: string, dest: ItemRef, placed = false) =>
  apiPost<ExcerptView>(`/excerpts/${encodeURIComponent(id)}/destinations`, { ...dest, placed });

export const removeDestination = (id: string, dest: ItemRef) =>
  apiDeleteJson<{ excerptNowEmpty: boolean }>(`/excerpts/${encodeURIComponent(id)}/destinations/${dest.type}/${encodeURIComponent(dest.id)}`);

export const deleteExcerpt = (id: string, deleteLinked = false) =>
  apiDelete(`/excerpts/${encodeURIComponent(id)}${deleteLinked ? '?deleteLinked=true' : ''}`);

export const deletePreflight = (type: SourceType, id: string) =>
  apiGet<{ excerptCount: number; destinationItemCount: number }>(`/excerpts/delete-preflight/${type}/${encodeURIComponent(id)}`);

/** "3 excerpts appear in 2 other items." for a delete confirmation, or '' when none. */
export async function excerptDeleteWarning(type: SourceType, id: string): Promise<string> {
  try {
    const { excerptCount: e, destinationItemCount: d } = await deletePreflight(type, id);
    if (!e) return '';
    return `${e} excerpt${e === 1 ? '' : 's'} from it appear${e === 1 ? 's' : ''} in ${d} other item${d === 1 ? '' : 's'} and will be removed from ${d === 1 ? 'it' : 'them'}.`;
  } catch {
    return '';
  }
}
